import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import type { FormatFirefliesMeetingResult } from "@/hooks/useFireflies";
import type { FirefliesTranscriptSummary } from "@/types/fireflies";
import {
  appendBatchFormatResult,
  openFirefliesBatchResults,
  setBatchProcessing,
  type BatchFormatResultItem,
} from "@/state/firefliesUiLegend";
import {
  appendAmbiguityNote,
  suggestAssignee,
} from "@/lib/clickup/assigneeRouting";

type FormatMutate = (transcriptId: string) => Promise<FormatFirefliesMeetingResult>;

function meetingTitle(meeting: FirefliesTranscriptSummary | undefined, id: string): string {
  return meeting?.title?.trim() || "Untitled meeting";
}

function applyCreditToasts(
  result: FormatFirefliesMeetingResult,
  toast: ReturnType<typeof useToast>["toast"],
  totals: { credits: number; insufficient: boolean }
) {
  if (result.creditError) return;
  totals.credits += result.creditsDeducted;
  if (!result.hadEnoughBalance) totals.insufficient = true;
}

export function useFirefliesBatchFormat(
  formatMutate: FormatMutate,
  meetingsById: Map<string, FirefliesTranscriptSummary>
) {
  const { toast } = useToast();

  const runSequentialFormat = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      const uniqueIds = [...new Set(ids)];
      const results: BatchFormatResultItem[] = [];
      const creditTotals = { credits: 0, insufficient: false };

      openFirefliesBatchResults([], {
        processing: {
          active: true,
          currentTranscriptId: uniqueIds[0],
          currentLabel: meetingTitle(meetingsById.get(uniqueIds[0]), uniqueIds[0]),
          completed: 0,
          total: uniqueIds.length,
        },
      });

      for (let i = 0; i < uniqueIds.length; i++) {
        const id = uniqueIds[i];
        const label = meetingTitle(meetingsById.get(id), id);

        setBatchProcessing({
          active: true,
          currentTranscriptId: id,
          currentLabel: label,
          completed: i,
          total: uniqueIds.length,
        });

        try {
          const result = await formatMutate(id);
          applyCreditToasts(result, toast, creditTotals);

          const suggestion = suggestAssignee(result.notes);
          const notesWithNote = {
            ...result.notes,
            parentTaskDescription: appendAmbiguityNote(
              result.notes.parentTaskDescription,
              suggestion
            ),
          };

          const item: BatchFormatResultItem = {
            transcriptId: id,
            meetingTitle: label,
            status: "success",
            notes: notesWithNote,
            editableTitle: result.notes.parentTaskTitle,
            selectedForClickUp: true,
            suggestedAssigneeId: suggestion.assigneeId,
            assigneeId: suggestion.assigneeId,
            assigneeReason: suggestion.reason,
          };
          results.push(item);
          appendBatchFormatResult(item);
        } catch (e) {
          const item: BatchFormatResultItem = {
            transcriptId: id,
            meetingTitle: label,
            status: "failed",
            error: e instanceof Error ? e.message : "Could not format meeting",
            editableTitle: "",
            selectedForClickUp: false,
            suggestedAssigneeId: null,
            assigneeId: null,
          };
          results.push(item);
          appendBatchFormatResult(item);
        }

        setBatchProcessing({
          active: i < uniqueIds.length - 1,
          currentTranscriptId: i < uniqueIds.length - 1 ? uniqueIds[i + 1] : null,
          currentLabel:
            i < uniqueIds.length - 1
              ? meetingTitle(meetingsById.get(uniqueIds[i + 1]), uniqueIds[i + 1])
              : "",
          completed: i + 1,
          total: uniqueIds.length,
        });
      }

      setBatchProcessing(null);

      const succeeded = results.filter((r) => r.status === "success").length;
      const failed = results.length - succeeded;

      if (creditTotals.credits > 0) {
        toast({
          title: "Credits Deducted",
          description: `${creditTotals.credits.toLocaleString()} credits used${uniqueIds.length > 1 ? ` for ${succeeded} meeting${succeeded !== 1 ? "s" : ""}` : ""}.`,
        });
        if (creditTotals.insufficient) {
          toast({
            title: "Insufficient Credits",
            description: "Balance is now 0. Purchase more credits to continue formatting.",
            variant: "destructive",
          });
        }
      }

      if (failed > 0 && succeeded > 0) {
        toast({
          title: "Batch complete with errors",
          description: `${succeeded} formatted, ${failed} failed. Review results in the dialog.`,
          variant: "destructive",
        });
      } else if (failed > 0 && succeeded === 0) {
        toast({
          title: "Batch format failed",
          description: `All ${failed} meeting${failed !== 1 ? "s" : ""} failed to format.`,
          variant: "destructive",
        });
      } else if (uniqueIds.length > 1) {
        toast({
          title: "Batch format complete",
          description: `${succeeded} meeting${succeeded !== 1 ? "s" : ""} ready to review.`,
        });
      }
    },
    [formatMutate, meetingsById, toast]
  );

  return { runSequentialFormat };
}
